import type { AxiosError, AxiosResponse } from "axios";
import type {
  GetHomeScreenDashboardV3Input,
  getHomeScreenV2Input,
  HomeScreenDashboardV3,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

export const getUpcomingActivities = async (resourceId: string) => {
  try {
    const url = `/msp/upcoming_activities/${resourceId}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;
    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getGeneralStatus = async (filter: string, timezone: string) => {
  try {
    const url = `/msp/home-screen/general/counts?filter=${filter}&time_zone=${timezone}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;
    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getOverview = async (
  resourceId: string,
  filter: string,
  timezone: string
) => {
  try {
    const url = `/msp/total-tickets-by-status/${resourceId}?filter=${filter}&time_zone=${timezone}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;

    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getTotalWorkHours = async (
  resourceId: string,
  filter: string,
  timezone: string
) => {
  try {
    const url = `/msp/total-hours-worked/${resourceId}?filter=${filter}&time_zone=${timezone}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;

    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getTotalService = async (
  resourceId: string,
  filter: string,
  timezone: string
) => {
  try {
    const url = `/msp/total-services-time-added/${resourceId}?filter=${filter}&time_zone=${timezone}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;

    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getTodaysTickets = async (timezone: string) => {
  try {
    const url = `/ticket/today?time_zone=${timezone}`;
    const responce = await axiosClient.get(url);
    const respData = responce?.data;

    if (respData.success === true && respData.statusCode === 200) {
      return respData.data;
    }
    throw new Error(respData.message);
  } catch (e) {
    throw e;
  }
};

export const getHomeScreenV2 = async ({
  resourceId,
  filter,
  timezone,
}: getHomeScreenV2Input) => {
  try {
    const url = `/v2/servicedesk/msp/home_screen_dashboard/${resourceId}?filter=${filter}&time_zone=${timezone}`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success === true && resData?.statusCode === 200) {
      return resData?.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (e) {
    throw e;
  }
};

/**
 * Queue counts for a set of team members, or all of them. Supersedes
 * `getHomeScreenV2`, which takes a single resource in its path — V2 stays live
 * only for the legacy home screen.
 *
 * `filter` and `time_zone` remain query params; only the resource selection is
 * in the body. `Content-Type` is explicit because without it Express skips the
 * body and answers with the missing-`resource_ids` 400.
 */
export const getHomeScreenDashboardV3 = async ({
  filter,
  timezone,
  all,
  resource_ids,
}: GetHomeScreenDashboardV3Input) => {
  try {
    const url = "/v3/servicedesk/msp/home_screen_dashboard";
    const response = await axiosClient.post(
      url,
      all ? { all: true } : { resource_ids },
      {
        params: { filter, time_zone: timezone },
        headers: { "Content-Type": "application/json" },
      },
    );
    const resData = response?.data;
    // Validation and permission failures arrive as HTTP 200 with the real status
    // in the body, and the 403 body carries no `success` field at all — so both
    // have to be checked.
    if (resData?.success === true && resData?.statusCode === 200) {
      return resData.data as HomeScreenDashboardV3;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
